import {
  type CaraWorkspaceFieldsFragment,
  CaraWorkspaceTool,
  type HtmlDocumentTemplateFieldsFragment
} from '@oysterjs/ais/api/graphql';

import {
  renderApplicationFileGeneratorEvent,
  renderDocxEditorEvent,
  renderXlsxEditorEvent,
  renderDraftDocumentEvent,
  renderDraftEmailEvent,
  renderDraftOutboundCallEvent,
  renderFileEditorEvent,
  renderFillTemplateEvent,
  renderPdfEditorEvent
} from './conversation-stage-artifact-event-renderers';
import { BulkEmailDraftEvent } from './conversation-bulk-email-draft-event';

/**
 * Dispatches a single `'artifact'`-classified stage event to its renderer. Only
 * the tools that produce a standalone artifact card reach this component;
 * `'row'`-classified events (thoughts, searches, integration writes) render
 * inside `StageEventGroup`, and `'hidden'`-classified events are filtered out
 * upstream by `getStageEventKind`. Keep the switch in sync with that classifier.
 */
export const ArtifactEvent = (props: {
  workspace: CaraWorkspaceFieldsFragment;
  event: CaraWorkspaceFieldsFragment['planStages'][0]['events'][0];
  htmlDocumentTemplates: HtmlDocumentTemplateFieldsFragment[];
  setShowArtifactFocusDialog: (artifact: CaraWorkspaceFieldsFragment['artifacts'][0]) => void;
}) => {
  const { event, workspace } = props;

  switch (event.toolCall?.type) {
    case CaraWorkspaceTool.DraftEmail:
      return renderDraftEmailEvent(event, workspace, props.setShowArtifactFocusDialog);
    case CaraWorkspaceTool.BulkEmailDraft:
      return <BulkEmailDraftEvent event={event} />;
    case CaraWorkspaceTool.DraftOutboundCall:
      return renderDraftOutboundCallEvent(event, workspace.id);
    case CaraWorkspaceTool.DraftDocument:
      return renderDraftDocumentEvent(event, workspace.id, props.htmlDocumentTemplates);
    case CaraWorkspaceTool.FillTemplate:
      return renderFillTemplateEvent(event, workspace, props.setShowArtifactFocusDialog);
    case CaraWorkspaceTool.FileEditor:
      return renderFileEditorEvent(event, workspace, props.setShowArtifactFocusDialog);
    case CaraWorkspaceTool.PdfEditor:
      return renderPdfEditorEvent(event, workspace, props.setShowArtifactFocusDialog);
    case CaraWorkspaceTool.ApplicationFileGenerator:
      return renderApplicationFileGeneratorEvent(
        event,
        workspace,
        props.setShowArtifactFocusDialog
      );
    case CaraWorkspaceTool.DocxEditor:
      return renderDocxEditorEvent(event, workspace, props.setShowArtifactFocusDialog);

    case CaraWorkspaceTool.XlsxEditor:
      return renderXlsxEditorEvent(event, workspace, props.setShowArtifactFocusDialog);
    default:
      return null;
  }
};
